import { GridColDef } from "@mui/x-data-grid";
import { AggregateScoreOrderField } from "~/operations";
import { TextCell } from "~/components/DataGridCells";
import { ScopeTableComplianceCell } from "./ScopeTableComplianceCell";
import { ScopeTableContactsCell } from "./ScopeTableContactsCell";
import { ScopeType } from "./hooks/useEntityScopes";

export type ScopeTableVariant = "policy" | "check";

export interface BaseScopeNode {
  id: string;
  name: string;
  spacesCount: number;
  assets?: number;
  completion?: number | null;
  findingStats?: {
    pass: number;
    fail: number;
    total: number;
  } | null;
  blastRadius?: {
    assets?: number | null;
    affected?: number | null;
  } | null;
  contacts?: Array<{
    contactType: string;
    identity: string;
    email?: string | null;
    name?: string | null;
  }>;
}

const columns = {
  name: {
    field: "name",
    headerName: "Entity", // Set via DYNAMIC_HEADERS
    headerClassName: "left-cell",
    cellClassName: "left-cell",
    minWidth: 250,
    flex: 1,
    resizable: false,
    renderCell: TextCell,
    sortable: true,
  },
  spacesCount: {
    field: "spacesCount",
    headerName: "Children", // Set via DYNAMIC_HEADERS
    width: 100,
    resizable: false,
    align: "left" as const,
    headerAlign: "left" as const,
    renderCell: TextCell,
    sortable: true,
  },
  assets: {
    field: "assets",
    headerName: "Assets",
    width: 100,
    resizable: false,
    align: "left" as const,
    headerAlign: "left" as const,
    renderCell: TextCell,
    sortable: true,
  },
  contacts: {
    field: "contacts",
    headerName: "Contacts",
    width: 200,
    resizable: false,
    renderCell: ScopeTableContactsCell,
    sortable: false,
  },
  compliance: {
    field: "completion",
    headerName: "Compliance", // Set via DYNAMIC_HEADERS
    headerClassName: "right-cell",
    cellClassName: "right-cell",
    width: 200,
    resizable: false,
    renderCell: ScopeTableComplianceCell,
    sortable: true,
  },
} satisfies Record<string, Partial<GridColDef>>;

type ColumnKey = keyof typeof columns;

const VARIANT_COLUMNS: Record<ScopeTableVariant, ColumnKey[]> = {
  policy: ["name", "spacesCount", "assets", "contacts", "compliance"],
  check: ["name", "spacesCount", "contacts", "compliance"],
};

// Dynamic headers by scope type and/or variant
// Use `null` to hide the column for that scope type
const DYNAMIC_HEADERS: Partial<
  Record<
    ColumnKey,
    Record<ScopeType, string | null> | Record<ScopeTableVariant, string>
  >
> = {
  name: {
    Organizations: "Organization",
    Spaces: "Space",
    Workspaces: "Workspace",
  },
  spacesCount: {
    Organizations: "Spaces",
    Spaces: "Workspaces",
    Workspaces: null, // Hidden for Workspaces scope
  },
  compliance: {
    policy: "Policy Compliance",
    check: "Check Compliance",
  },
};

/**
 * Get column definitions for a scope table.
 * @param scopeType - The current scope type (Organizations, Spaces, Workspaces)
 * @param variant - The table variant ("policy" or "check")
 */
export function getColumns<T extends BaseScopeNode>(
  scopeType: ScopeType,
  variant: ScopeTableVariant,
): GridColDef<T>[] {
  return VARIANT_COLUMNS[variant]
    .map((key) => {
      const col = columns[key];
      const dynamicHeader = DYNAMIC_HEADERS[key] as
        | Record<string, string | null>
        | undefined;

      // Check if column should be hidden for this scope type
      const scopeHeader = dynamicHeader?.[scopeType];
      if (scopeHeader === null) return null;

      const headerName =
        scopeHeader ?? dynamicHeader?.[variant] ?? col.headerName;

      return { ...col, headerName } as GridColDef<T>;
    })
    .filter((col): col is GridColDef<T> => col !== null);
}

/**
 * Map of field names to their GraphQL sort fields.
 * Only includes columns that support server-side sorting.
 */
export const SORT_FIELD_MAP: Record<string, AggregateScoreOrderField> = {
  name: AggregateScoreOrderField.Title,
  spacesCount: AggregateScoreOrderField.NumChildren,
  assets: AggregateScoreOrderField.AssetCount,
  completion: AggregateScoreOrderField.Completion,
};
